import type { ContactConnectionWithContact } from "./templateDictionaryOptions";
import {
  formatAddress,
  formatAddressLine1,
  formatAddressLine2,
  formatContactsName,
  formatDate,
  formatLocalOrIntlPhoneNumber,
} from "@/lib/utils/format";

/** The contact projection used by Generate Document's Apply Contact sections. */
export const getContactTemplateFieldValue = (
  connection: ContactConnectionWithContact,
  normalizedBase: string,
): string | undefined => {
  const addr = connection.contact.addresses?.[0];
  const email = connection.contact.emails?.[0];
  const phone = connection.contact.phones?.[0];
  const fax = connection.contact.faxes?.[0];

  switch (normalizedBase) {
    case "contactName":
      return formatContactsName(connection.contact) ?? "";
    case "contactPronoun": {
      const gender = connection.contact.individualGender;
      return gender === "Male" ? "he" : gender === "Female" ? "she" : "they";
    }
    case "contactPossessivePronoun": {
      const gender = connection.contact.individualGender;
      return gender === "Male" ? "his" : gender === "Female" ? "her" : "their";
    }
    case "contactObjectivePronoun": {
      const gender = connection.contact.individualGender;
      return gender === "Male" ? "him" : gender === "Female" ? "her" : "them";
    }
    case "contactFirstName":
      return connection.contact.individualFirstName ?? "";
    case "contactLastName":
      return connection.contact.individualLastName ?? "";
    case "contactMiddleName":
      return connection.contact.individualMiddleName ?? "";
    case "contactTitle":
      return (
        connection.contact.individualTitle ?? connection.contact.title ?? ""
      );
    case "contactEmail":
      return email?.address ?? "";
    case "contactPhone":
      return formatLocalOrIntlPhoneNumber(phone);
    case "contactDateOfBirth": {
      const dob = connection.contact.individualDateOfBirth;
      return formatDate(dob as string | Date) ?? "";
    }
    case "contactFax":
      return formatLocalOrIntlPhoneNumber(fax);
    case "contactAddress":
      return formatAddress({
        street1: addr?.street1 ?? "",
        street2: addr?.street2 ?? undefined,
        city: addr?.city ?? "",
        state: addr?.state ?? "",
        zipCode: addr?.zipCode ?? "",
        country: addr?.country ?? "",
      });
    case "contactAddressLine1":
      return formatAddressLine1({
        street1: addr?.street1 ?? "",
        street2: addr?.street2 ?? undefined,
      });
    case "contactAddressLine2":
      return formatAddressLine2({
        city: addr?.city ?? undefined,
        state: addr?.state ?? undefined,
        zipCode: addr?.zipCode ?? undefined,
      });
    case "contactAddressStreet1":
      return addr?.street1 ?? "";
    case "contactAddressStreet2":
      return addr?.street2 ?? "";
    case "contactAddressCity":
      return addr?.city ?? "";
    case "contactAddressState":
      return addr?.state ?? "";
    case "contactAddressZipCode":
      return addr?.zipCode ?? "";
    case "contactAddressCountry":
      return addr?.country ?? "";
    case "contactSSN":
      return connection.contact.individualSocialSecurityNumber ?? "";
    case "contactDriverLicenseNumber":
      return connection.contact.individualDriverLicenseNumber ?? "";
    case "contactDriverLicenseState":
      return connection.contact.individualDriverLicenseState ?? "";
    default:
      return undefined;
  }
};

const contactSectionRoles: Record<string, string> = {
  client: "Client",
  defendant: "Defendant",
  witness: "Witness",
  primaryContact: "Primary Contact",
  incidentReportSource: "Incident Report Source",
  provider: "Provider",
  bodilyInjuryAdjuster: "Bodily Injury Adjuster",
  adjuster: "Bodily Injury Adjuster",
  propertyDamageAdjuster: "Property Damage Adjuster",
  medPayAdjuster: "MedPay Adjuster",
  adjusterSupervisor: "Adjuster Supervisor",
};

/** Match section auto-apply: one connection, using the full selection pool. */
export const getAutoAppliedContactTemplateFieldValue = (
  connections: ContactConnectionWithContact[] | undefined,
  sectionKey: string,
  normalizedBase: string,
): string | undefined => {
  if (!connections) return undefined;
  const role = contactSectionRoles[sectionKey];
  if (sectionKey !== "contact" && !role) return undefined;

  const candidates = role
    ? connections.filter((connection) => connection.label === role)
    : connections;
  return candidates.length === 1
    ? getContactTemplateFieldValue(candidates[0], normalizedBase)
    : undefined;
};
